import { SOCKET_EVENT_NAMES } from '@/constants/socket.events';
import { notify } from '@/helpers/index';
import routeNames from '@/routes/routeNames';
import {
  Campaign,
  CampaignStatusStats,
  EmailCampaignFilter,
  EmailMarketingStats,
  emailMarketingStore,
  SetupResponse,
  Template,
} from '@/state/EmailMarketingStore';
import { SocketResponse } from '@/types/socketResponse.type';
import { useCustomNavigation } from '../useNavigation';
import {
  CloneCampaignData,
  CloneCampaignResponse,
  SearchEmailCampaignType,
  EmailTestData,
  FilterTemplateData,
  JobData,
  GetTemplateResponse,
  GetCampaignsResponse,
  UpdateCampaignPayload,
  CampaignDetails,
  SaveAndSendCampaignPayload,
  SaveTemplatePayload,
  VerifyDomainPayload,
  MoreTemplatesResponse,
  MoreCampaignsResponse,
} from '@/pages/EmailMarketing/types';
import { TemplateFilters } from '@/types/templateFilters.types';
import Logger from '@/config/Logger';
import { SocketRequest } from '@/config/socket';

type GetCampaignsParams = {
  page?: number;
  limit?: number;
  filter?: EmailCampaignFilter;
};

type GetEmailCampaignsByMonthParams = {
  year: string;
  month: string;
  limit?: number;
};

const PAGE_SIZE = 10;

export const useEmailMarketing = () => {
  const navigate = useCustomNavigation();

  const checkDomain = async () => {
    try {
      emailMarketingStore.setLoading(true);
      emailMarketingStore.setShowSpinner(false);
      const response: SocketResponse<SetupResponse> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_DOMAIN_RECORDS,
        { data: {} },
      );
      if (response.error) {
        notify('error', response?.error as string);
      } else if (response?.data) {
        emailMarketingStore.setSetupResponse(response?.data);
        emailMarketingStore.setVerificationStatus(response?.data?.status);
        if (response?.data?.verified) {
          emailMarketingStore.setReady();
          emailMarketingStore.setIsDomainVerified(true);
        }
      }
    } catch (error) {
      Logger.error("Error in 'checkDomain':", error);
      notify('error', 'Error checking domain');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getDomain = async () => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<VerifyDomainPayload> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_DOMAIN,
        { data: {} },
      );
      if (response.error) {
        notify('error', response?.error as string);
      } else if (response?.data) {
        emailMarketingStore.set_sender_email(response?.data?.domain);
      }
    } catch (error) {
      Logger.error("Error in 'getDomain':", error);
      notify('error', 'Error getting domain');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const disconnectDomain = async () => {
    try {
      emailMarketingStore.setShowSpinner(true);
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<void> = await SocketRequest(
        SOCKET_EVENT_NAMES.DISCONNECT_DOMAIN,
        { data: {} },
      );
      if (response.error) {
        notify('error', response?.error as string);
      } else {
        emailMarketingStore.setSetupResponse(null);
        emailMarketingStore.setVerificationStatus('awaiting-submission');
      }
    } catch (error) {
      Logger.error("Error in 'disconnectDomain':", error);
      notify('error', 'Error disconnecting domain');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const verifyDomain = async (values: VerifyDomainPayload) => {
    try {
      emailMarketingStore.setLoading(true);
      emailMarketingStore.setShowSpinner(false);
      const response: SocketResponse<SetupResponse> = await SocketRequest(
        SOCKET_EVENT_NAMES.VERIFY_DOMAIN,
        { data: values },
      );
      if (response.error) {
        notify('error', response?.error as string);
      } else if (response?.data) {
        emailMarketingStore.setVerificationStatus('pending');
        emailMarketingStore.setSetupResponse(response?.data);
      }
    } catch (error) {
      Logger.error("Error in 'verifyDomain':", error);
      notify('error', 'Error verifying domain');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const saveTemplate = async (data: SaveTemplatePayload) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<Template> = await SocketRequest(
        SOCKET_EVENT_NAMES.SAVE_EMAIL_TEMPLATE,
        {
          data: data,
        },
      );
      if (!response.error) {
        notify('success', 'Saved successfully');
        emailMarketingStore.setTemplate(response?.data);
        if (response?.data?._id) {
          emailMarketingStore.setTemplateId(response?.data?._id);
        }
      } else {
        notify('error', response.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'saveTemplate':", error);
      notify('error', 'Error saving template');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const cloneTemplate = async (
    data: SaveTemplatePayload,
    filters: TemplateFilters,
  ) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<null> = await SocketRequest(
        SOCKET_EVENT_NAMES.SAVE_EMAIL_TEMPLATE,
        {
          data: data,
        },
      );
      if (!response.error) {
        notify('success', 'Saved successfully');
        await getTableTemplates(filters);
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'cloneTemplate':", error);
      notify('error', 'Error cloning template');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getTemplates = async (page?: number, limit?: number) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<GetTemplateResponse> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_EMAIL_TEMPLATES,
        {
          data: { page, limit },
        },
      );

      if (response.data) {
        const totalCount = response?.data?.count;
        const hasMore = totalCount > response?.data?.page * PAGE_SIZE;
        emailMarketingStore.setTemplateMetaData({
          count: totalCount,
          page: response?.data?.page,
          hasMore,
        });
        emailMarketingStore.setTemplates(response?.data?.templates);
      }
    } catch (error) {
      Logger.error("Error in 'getTemplates':", error);
      notify('error', 'Error getting templates');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getTableTemplates = async ({
    search,
    type,
    tags,
    page,
    limit,
  }: TemplateFilters) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<Template> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_EMAIL_TEMPLATES,
        {
          data: { search, type, tags, page, limit },
        },
      );
      emailMarketingStore.setTemplates(response?.data);
    } catch (error) {
      Logger.error("Error in 'getTableTemplates':", error);
      notify('error', 'Error getting table templates');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getMoreTemplates = async (page?: number, limit?: number) => {
    try {
      emailMarketingStore.setMoreTemplateLoader(true);
      const response: SocketResponse<MoreTemplatesResponse> =
        await SocketRequest(SOCKET_EVENT_NAMES.GET_EMAIL_TEMPLATES, {
          data: { page, limit },
        });

      if (response.data) {
        const totalCount = response?.data?.count;
        const hasMore = totalCount > response?.data?.page * PAGE_SIZE;
        emailMarketingStore.setTemplateMetaData({
          count: totalCount,
          page: response?.data?.page,
          hasMore,
        });
        const allTemplates = [
          ...new Map([
            ...(emailMarketingStore.templates ?? []).map(template => [
              template?._id,
              template,
            ]),
            ...response.data.templates.map((template: Template) => [
              template?._id,
              template,
            ]),
          ]).values(),
        ];
        emailMarketingStore.setTemplates(allTemplates);
      }
    } catch (error) {
      Logger.error("Error in 'getMoreTemplates':", error);
      notify('error', 'Error getting more templates');
    } finally {
      emailMarketingStore.setMoreTemplateLoader(false);
    }
  };

  const deleteTemplate = async (id: string, filters: TemplateFilters) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<null> = await SocketRequest(
        SOCKET_EVENT_NAMES.DELETE_EMAIL_TEMPLATE,
        { data: { id } },
      );
      if (!response.error) {
        await getTableTemplates(filters);
        notify('success', 'Deleted successfully');
      } else {
        notify('error', response.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'deleteTemplate':", error);
      notify('error', 'Error deleting template');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const createCampaign = async (data: CampaignDetails) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<null> = await SocketRequest(
        SOCKET_EVENT_NAMES.CREATE_EMAIL_CAMPAIGN,
        {
          data: data,
        },
      );
      if (!response.error) {
        notify('success', 'Saved successfully');
        await getCampaigns({
          filter: EmailCampaignFilter.ALL,
        });
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'createCampaign':", error);
      notify('error', 'Error creating campaign');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getCurrentCampaign = async () => {
    try {
      emailMarketingStore.setFetchCurrentCampaign(true);
      const response: SocketResponse<JobData[]> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_CURRENT_CAMPAIGN,
        {
          data: {},
        },
      );
      if (Array.isArray(response.data)) {
        response.data.forEach((job: JobData) => {
          const progressData = job?.progress?.data;

          if (!progressData?.campaignId) return;

          const campaign = emailMarketingStore?.campaigns?.find(
            campaign => campaign?._id == progressData?.campaignId,
          );

          if (campaign) {
            campaign.percentage = progressData?.percentage ?? 0;
            campaign.status = progressData?.status ?? 'unknown';
          }
        });
        emailMarketingStore.setCampaignSendingInProgress(true);
      } else {
        Logger.error("Couldn't get campaigns in progress");
      }
    } catch (error) {
      Logger.error("Error in 'getCurrentCampaign':", error);
    } finally {
      emailMarketingStore.setFetchCurrentCampaign(false);
    }
  };

  const getEmailCampaignsByMonth = async ({
    year,
    month,
    limit = 100,
  }: GetEmailCampaignsByMonthParams) => {
    try {
      emailMarketingStore.setCampaignMonthLoader(true);
      const response: SocketResponse<{ campaignsByMonth: Campaign[] }> =
        await SocketRequest(SOCKET_EVENT_NAMES.GET_EMAIL_CAMPAIGNS_BY_MONTH, {
          data: { year, month, limit },
        });
      if (response.data) {
        emailMarketingStore.setCampaignsByMonth(response?.data ?? []);
      } else if (response?.error) {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'getEmailCampaignsByMonth':", error);
      notify('error', 'Failed to fetch monthly email campaigns');
    } finally {
      emailMarketingStore.setCampaignMonthLoader(false);
    }
  };

  const getCampaigns = async ({
    page = 1,
    limit = 10,
    filter = EmailCampaignFilter.ALL,
  }: GetCampaignsParams) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<GetCampaignsResponse> =
        await SocketRequest(SOCKET_EVENT_NAMES.GET_EMAIL_CAMPAIGNS, {
          data: { page, limit, filter },
        });
      if (response?.data) {
        const totalCount = response?.data?.total;
        const hasMore = totalCount > response?.data?.page * PAGE_SIZE;
        emailMarketingStore.setCampaignMetaData({
          count: totalCount,
          page: response?.data?.page,
          hasMore,
        });
        emailMarketingStore.setCampaigns(response?.data?.campaigns ?? []);
      }
    } catch (error) {
      Logger.error("Error in 'getCampaigns':", error);
      notify('error', 'Error getting campaigns');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getMoreCampaigns = async ({
    page = 1,
    limit = 10,
    filter = EmailCampaignFilter.ALL,
  }: GetCampaignsParams) => {
    try {
      emailMarketingStore.setCampaignPageListLoader(true);
      const response: SocketResponse<MoreCampaignsResponse> =
        await SocketRequest(SOCKET_EVENT_NAMES.GET_EMAIL_CAMPAIGNS, {
          data: { page, limit, filter },
        });

      if (response.data) {
        const totalCount = response?.data?.total;
        const hasMore = totalCount > response?.data?.page * PAGE_SIZE;

        emailMarketingStore.setCampaignMetaData({
          count: totalCount,
          page: response?.data?.page,
          hasMore,
        });
        const allCampaigns = [
          ...new Map([
            ...emailMarketingStore.campaigns.map(campaign => [
              campaign._id,
              campaign,
            ]),
            ...response.data.campaigns.map((campaign: Campaign) => [
              campaign?._id,
              campaign,
            ]),
          ]).values(),
        ];
        emailMarketingStore.setCampaigns(allCampaigns);
      }
    } catch (error) {
      Logger.error("Error in 'getMoreCampaigns':", error);
      notify('error', 'Error getting more campaigns');
    } finally {
      emailMarketingStore.setCampaignPageListLoader(false);
    }
  };

  const filterCampaigns = async (filter: SearchEmailCampaignType) => {
    try {
      emailMarketingStore.setCampaignListLoader(true);
      const response: SocketResponse<Campaign[]> = await SocketRequest(
        SOCKET_EVENT_NAMES.FILTER_CAMPAIGNS,
        {
          data: filter,
        },
      );
      if (response?.data) {
        emailMarketingStore.setFilteredCampaigns(response?.data);
      }
    } catch (error) {
      Logger.error("Error in 'filterCampaigns':", error);
      notify('error', 'Error filtering campaigns');
    } finally {
      emailMarketingStore.setCampaignListLoader(false);
    }
  };

  const filterTemplates = async (filter: FilterTemplateData) => {
    try {
      emailMarketingStore.setTemplateLoader(true);
      const response: SocketResponse<Template[]> = await SocketRequest(
        SOCKET_EVENT_NAMES.FILTER_TEMPLATES,
        {
          data: filter,
        },
      );
      emailMarketingStore.setFilteredTemplates(response?.data);
    } catch (error) {
      Logger.error("Error in 'filterTemplates':", error);
      notify('error', 'Error filtering templates');
    } finally {
      emailMarketingStore.setTemplateLoader(false);
    }
  };

  const getCampaign = async (id: string) => {
    try {
      const response: SocketResponse<Campaign> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_EMAIL_CAMPAIGN,
        { data: { id } },
      );
      emailMarketingStore.setCampaign(response?.data);
    } catch (error) {
      Logger.error("Error in 'getCampaign':", error);
      notify('error', 'Error getting campaign');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const editCampaign = async (id: string, newTitle: string) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<null> = await SocketRequest(
        'edit_email_campaign',
        { data: { id, newTitle } },
      );
      if (!response.error) {
        notify('success', 'Saved successful');
        await getCampaigns({
          filter: EmailCampaignFilter?.ALL,
        });
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'editCampaign':", error);
      notify('error', 'Error editing campaign');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const updateCampaign = async (data: UpdateCampaignPayload) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<null> = await SocketRequest(
        SOCKET_EVENT_NAMES.UPDATE_EMAIL_CAMPAIGN,
        {
          data: data,
        },
      );
      if (response.data) {
        notify('success', 'Saved successful');
        await getCampaigns({
          filter: EmailCampaignFilter?.ALL,
        });
      } else {
        notify(
          'error',
          (response?.error as string) || 'Error updating campaign',
        );
      }
    } catch (error) {
      Logger.error("Error in 'updateCampaign':", error);
      notify('error', 'Error updating campaign');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };
  const testEmail = async (email: EmailTestData) => {
    try {
      emailMarketingStore.setSendingTestEmail(true);
      const response: SocketResponse<null> = await SocketRequest(
        SOCKET_EVENT_NAMES.SEND_TEST_EMAIL,
        {
          data: email,
        },
      );
      if (!response?.error) {
        notify('success', 'Sent successfully');
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'testEmail':", error);
      notify('error', 'Error sending test email');
    } finally {
      emailMarketingStore.setSendingTestEmail(false);
    }
  };

  const saveAndSendCampaign = async (data: SaveAndSendCampaignPayload) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<any> = await SocketRequest(
        SOCKET_EVENT_NAMES.SAVE_AND_SEND_EMAIL_CAMPAIGN,
        {
          data: data,
        },
      );
      if (!response.error) {
        const draftDate = response?.data?.draftDate;
        const isToday = draftDate
          ? new Date(draftDate).toDateString() === new Date().toDateString()
          : false;
        if (isToday) {
          notify('success', 'Campaign will be sent shortly..', {
            id: `email-campaign-toast-${response?.data?._id}`,
            duration: 3000,
          });
        } else {
          notify('success', 'Campaign successfully Scheduled');
        }
        emailMarketingStore.setCampaignSendingInProgress(true);
        await getCampaigns({
          filter: EmailCampaignFilter.ALL,
        });
        navigate(`${routeNames.dashboard.emailMarketing}/campaigns`, {
          replace: true,
        });
        emailMarketingStore.setIsEmailCampaignSending(false);
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'saveAndSendCampaign':", error);
      notify('error', 'Error saving and sending campaign');
    } finally {
      emailMarketingStore.setLoading(false);
      emailMarketingStore.setIsEmailCampaignSending(false);
    }
  };

  const getEmailMarketingStats = async () => {
    try {
      const response: SocketResponse<EmailMarketingStats> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_EMAIL_MARKETING_STATS,
        {
          data: null,
        },
      );

      if (response?.data)
        emailMarketingStore.setEmailMarketingStats(response?.data);
    } catch (error) {
      Logger.error("Error in 'getEmailMarketingStats':", error);
      notify('error', 'Error getting email marketing stats');
    }
  };

  const deleteCampaign = async (id: string) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<null> = await SocketRequest(
        SOCKET_EVENT_NAMES.DELETE_EMAIL_CAMPAIGN,
        { data: { id } },
      );
      if (!response?.error) {
        notify('success', 'Deleted successfully');
        await getCampaigns({
          filter: EmailCampaignFilter?.ALL,
        });
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'deleteCampaign':", error);
      notify('error', 'Error deleting campaign');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  const getEmailCampaignStatusStats = async () => {
    try {
      const response: SocketResponse<CampaignStatusStats> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_EMAIL_CAMPAIGN_STATUS_STATS,
        {
          data: {},
        },
      );
      if (response?.data)
        emailMarketingStore.setEmailCampaignStatusStats(response?.data);
    } catch (error) {
      Logger.error("Error in 'getEmailCampaignStatusStats':", error);
      notify('error', 'Error getting campaign status stats');
    }
  };

  const cloneEmailCampaign = async (data: CloneCampaignData) => {
    try {
      emailMarketingStore.setLoading(true);
      const response: SocketResponse<CloneCampaignResponse> =
        await SocketRequest(SOCKET_EVENT_NAMES.CLONE_EMAIL_CAMPAIGN, {
          data,
        });
      if (!response?.error) {
        notify('success', 'Campaign cloned successfully');
        await getCampaigns({
          filter: EmailCampaignFilter?.ALL,
        });
      } else {
        notify('error', response?.error as string);
      }
    } catch (error) {
      Logger.error("Error in 'cloneEmailCampaign':", error);
      notify('error', 'Error cloning campaign');
    } finally {
      emailMarketingStore.setLoading(false);
    }
  };

  return {
    checkDomain,
    getDomain,
    verifyDomain,
    disconnectDomain,
    getTemplates,
    getTableTemplates,
    getEmailMarketingStats,
    getEmailCampaignStatusStats,
    deleteTemplate,
    saveTemplate,
    cloneTemplate,
    createCampaign,
    getCampaigns,
    getCampaign,
    updateCampaign,
    saveAndSendCampaign,
    testEmail,
    deleteCampaign,
    filterCampaigns,
    filterTemplates,
    getMoreCampaigns,
    getMoreTemplates,
    editCampaign,
    getCurrentCampaign,
    cloneEmailCampaign,
    getEmailCampaignsByMonth,
  };
};
